import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';

import { quando, useMaintenanceStatus } from '@/features/status/useMaintenanceStatus';
import { ApiError } from '@/shared/api/client';
import {
  acesso,
  workspaces as workspacesApi,
  type Workspace,
} from '@/shared/api/resources';
import { ErrorCode } from '@/shared/api/types';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Select } from '@/shared/ui/Select';

import { PublicFrame } from './PublicFrame';
import { POLITICA_DE_SENHA } from './senha';
import { usePortalConfig } from './usePortalConfig';

/**
 * Mensagem da credencial recusada.
 *
 * Com a mensagem de tentativas ligada (o comportamento do OnPoint, padrao da
 * instalacao), o servidor diz quantas tentativas faltam antes do bloqueio. O numero
 * e contado do mesmo jeito para conta que existe e para conta que nao existe, e por
 * isso a mensagem nao revela quem tem conta.
 */
function credencialRecusada(error: ApiError): string {
  const restantes = error.details?.['remaining_attempts'];
  if (typeof restantes !== 'number') {
    return 'Email ou senha incorretos.';
  }
  if (restantes <= 0) {
    return 'Email ou senha incorretos. A conta foi bloqueada temporariamente por tentativas seguidas.';
  }
  return restantes === 1
    ? 'Email ou senha incorretos. Voce tem 1 tentativa antes de a conta ser bloqueada temporariamente.'
    : `Email ou senha incorretos. Voce tem ${restantes} tentativas antes de a conta ser bloqueada temporariamente.`;
}

/**
 * Traduz o codigo de erro em uma mensagem util.
 *
 * A interface nao repete a mensagem tecnica do servidor: ela decide o texto a
 * partir do codigo, que e estavel. Assim, mudar a redacao no backend nao altera o
 * que o usuario le, e o texto pode ser especifico para a tela.
 */
function messageFor(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Nao foi possivel concluir o acesso. Tente novamente.';
  }

  switch (error.code) {
    case ErrorCode.InvalidCredentials:
      return credencialRecusada(error);
    case ErrorCode.AccountLocked:
      return 'Conta temporariamente bloqueada por tentativas seguidas. Aguarde alguns minutos.';
    case ErrorCode.TenantInactive:
      return 'A empresa esta inativa. Fale com o administrador.';
    case ErrorCode.PermissionDenied:
      // A conta do cadastro publico que ainda nao confirmou o e-mail (ADR 0096). O
      // servidor so diz isso depois da senha conferida.
      if (error.details?.['reason'] === 'ativacao_pendente') {
        const destino = error.details['email_masked'];
        return typeof destino === 'string' && destino
          ? `Confirme seu e-mail pelo link de ativacao que enviamos para ${destino}. O acesso so vale depois da ativacao.`
          : 'Confirme seu e-mail pelo link de ativacao que enviamos. O acesso so vale depois da ativacao.';
      }
      // A senha que passou do prazo de validade da empresa (R-1), sem a troca na tela
      // (servidor anterior ao ADR 0074), e trocada pelo link.
      if (error.details?.['reason'] === 'senha_vencida') {
        return 'Sua senha passou do prazo de validade definido pela empresa. Use "Esqueci minha senha" para criar uma senha nova.';
      }
      // A pessoa da assessoria so entra com o acesso da assessoria ligado (P-ACE-2).
      return error.details?.['reason'] === 'assessoria_inativa'
        ? 'A assessoria esta inativa. Fale com a seguradora.'
        : 'O acesso da assessoria nao esta disponivel nesta empresa.';
    case ErrorCode.RateLimited:
      return 'Muitas tentativas em pouco tempo. Aguarde um instante.';
    case ErrorCode.ValidationFailed:
      return error.message;
    case ErrorCode.NetworkUnavailable:
      return 'Sem conexao com o servidor. Verifique a rede e tente novamente.';
    default:
      return 'Nao foi possivel concluir o acesso. Tente novamente.';
  }
}

/** A recusa da conta que ainda nao ativou o acesso pelo link do e-mail (ADR 0096). */
function ativacaoPendente(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.code === ErrorCode.PermissionDenied &&
    error.details?.['reason'] === 'ativacao_pendente'
  );
}

/**
 * Pedido de um link novo de ativacao, na propria tela de acesso (ADR 0096).
 *
 * A resposta do servidor e a mesma exista ou nao a conta pendente, e a tela tambem: diz
 * que, se houver conta pendente, um link novo foi enviado.
 */
function ReenvioDaAtivacao({ email }: { email: string }) {
  const [situacao, setSituacao] = useState<
    | { tipo: 'parado' }
    | { tipo: 'enviando' }
    | { tipo: 'enviado'; horas: number; semEmail: boolean }
    | { tipo: 'erro'; mensagem: string }
  >({ tipo: 'parado' });

  async function reenviar() {
    setSituacao({ tipo: 'enviando' });
    try {
      const recibo = await acesso.resendActivation(email.trim());
      setSituacao({
        tipo: 'enviado',
        horas: recibo.validity_hours,
        semEmail: !recibo.email_delivery_available,
      });
    } catch (causa) {
      setSituacao({
        tipo: 'erro',
        mensagem:
          causa instanceof ApiError && causa.code === ErrorCode.RateLimited
            ? 'Muitos pedidos em pouco tempo. Aguarde alguns minutos e confira a caixa de entrada.'
            : 'Nao foi possivel pedir um link novo agora. Tente de novo em alguns minutos.',
      });
    }
  }

  if (situacao.tipo === 'enviado') {
    return situacao.semEmail ? (
      <Alert tone="warning">
        O envio de e-mail nao esta disponivel agora. Fale com a seguradora.
      </Alert>
    ) : (
      <Alert tone="success">
        Se a conta estiver esperando a ativacao, um link novo foi enviado para o e-mail
        informado. Ele vale por {situacao.horas} horas, e o link anterior deixou de
        valer.
      </Alert>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {situacao.tipo === 'erro' ? (
        <Alert tone="danger">{situacao.mensagem}</Alert>
      ) : null}
      <Button
        type="button"
        variant="secondary"
        loading={situacao.tipo === 'enviando'}
        onClick={() => void reenviar()}
      >
        Reenviar o link de ativacao
      </Button>
    </div>
  );
}

/**
 * Troca da senha vencida na propria tela de acesso (ADR 0074).
 *
 * O servidor conferiu a senha vencida e devolveu um segredo de uso unico, de prazo
 * curto, que grava a senha nova sem depender do e-mail. O segredo fica so na memoria
 * da tela, e nao no endereco.
 */
function trocaDaSenhaVencida(error: unknown): string | null {
  if (
    error instanceof ApiError &&
    error.code === ErrorCode.PermissionDenied &&
    error.details?.['reason'] === 'senha_vencida'
  ) {
    const segredo = error.details['password_change_token'];
    return typeof segredo === 'string' && segredo !== '' ? segredo : null;
  }
  return null;
}

function TrocaDaSenhaVencida({
  segredo,
  onTrocada,
  onCancelar,
}: {
  segredo: string;
  onTrocada: (senha: string) => Promise<void>;
  onCancelar: () => void;
}) {
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    void trocar();
  }

  async function trocar() {
    setErro(null);
    if (senha !== confirmacao) {
      setErro('As duas senhas precisam ser iguais.');
      return;
    }
    setEnviando(true);
    try {
      await acesso.resetPassword(segredo, senha);
    } catch (causa) {
      setEnviando(false);
      if (causa instanceof ApiError && causa.code === ErrorCode.ValidationFailed) {
        setErro(causa.message);
      } else if (causa instanceof ApiError && causa.code === ErrorCode.NotFound) {
        setErro(
          'O prazo para criar a senha nova acabou. Entre de novo com a senha atual.',
        );
      } else if (causa instanceof ApiError && causa.code === ErrorCode.RateLimited) {
        setErro('Muitas tentativas em pouco tempo. Aguarde um instante.');
      } else {
        setErro('Nao foi possivel trocar a senha. Tente novamente.');
      }
      return;
    }
    // A senha nova ja vale: o acesso segue com ela.
    try {
      await onTrocada(senha);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-4">
      <Alert tone="warning" title="Senha vencida">
        Sua senha passou do prazo de validade definido pela empresa. Crie uma senha nova
        para entrar. O prazo para isso e curto; depois dele, entre de novo com a senha
        atual.
      </Alert>
      {erro ? <Alert tone="danger">{erro}</Alert> : null}
      <Field
        label="Nova senha"
        type="password"
        name="new_password"
        required
        autoComplete="new-password"
        value={senha}
        hint={POLITICA_DE_SENHA}
        onChange={(evento) => setSenha(evento.target.value)}
      />
      <Field
        label="Confirmar nova senha"
        type="password"
        name="new_password_confirmation"
        required
        autoComplete="new-password"
        value={confirmacao}
        onChange={(evento) => setConfirmacao(evento.target.value)}
      />
      <Button type="submit" size="lg" loading={enviando} className="w-full">
        Salvar nova senha e entrar
      </Button>
      <Button type="button" variant="ghost" onClick={onCancelar}>
        Voltar ao acesso
      </Button>
    </form>
  );
}

/** Titulo de cada variante da tela de acesso. */
const titulos = {
  principal: 'Acessar a plataforma',
  corretor: 'Acesso do corretor',
  assessor: 'Acesso da assessoria',
} as const;

/**
 * Tela de acesso.
 *
 * Atende /entrar, /entrar/:empresa, /entrar/:empresa/corretor e
 * /entrar/:empresa/assessor, como o /login, o /login/:companyDns, o /acesso-corretor e
 * o /login/assessor do OnPoint. A marca vem da configuracao do portal da empresa,
 * quando ela publicou; o identificador da rota vai junto no acesso, para que quem
 * trabalha em mais de uma empresa entre na da tela.
 *
 * A assessoria (P-ACE-2) entra pelo mesmo acesso: o servidor reconhece a pessoa
 * ligada a uma assessoria e abre a sessao sem corretora, e a tela inicial leva a
 * escolha da corretora.
 */
export function LoginPage({
  variante = 'principal',
}: {
  variante?: 'principal' | 'corretor' | 'assessor';
}) {
  const { status, signIn } = useAuth();
  const location = useLocation();
  const { tenantSlug: slugDaRota } = useParams();
  const { portal } = usePortalConfig(slugDaRota);
  const manutencao = useMaintenanceStatus();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  // As empresas so aparecem depois que a senha e conferida pelo servidor. Perguntar
  // antes diria a qualquer um onde a pessoa trabalha.
  const [empresas, setEmpresas] = useState<Workspace[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Segredo da troca da senha vencida, so na memoria da tela (ADR 0074).
  const [trocaVencida, setTrocaVencida] = useState<string | null>(null);
  // A conta conferida que ainda nao ativou o acesso pelo e-mail (ADR 0096).
  const [pendente, setPendente] = useState(false);

  if (status === 'authenticated') {
    // Sem destino guardado, o acesso vai a tela inicial do perfil (P-USU-1), como o
    // /redirect-to-home do OnPoint. Ver features/users/TelaInicial.tsx.
    const from = (location.state as { from?: string } | null)?.from ?? '/inicio';
    return <Navigate to={from} replace />;
  }

  const aviso = (location.state as { aviso?: string } | null)?.aviso;
  const slugDoAcesso = portal?.tenant_slug ?? slugDaRota ?? '';
  const reconhecida = portal?.resolved ? portal : null;
  const corretorFechado =
    reconhecida !== null &&
    ((variante === 'corretor' && !reconhecida.enable_corretor_access) ||
      (variante === 'assessor' && !reconhecida.enable_advisor_insurance));
  // Autocadastro publico da corretora (P-ACE-2): so a empresa que publicou a tela e
  // ligou o autocadastro mostra o caminho.
  const autocadastro =
    reconhecida?.enable_registration_broker && slugDoAcesso
      ? `/entrar/${encodeURIComponent(slugDoAcesso)}/cadastro-corretora`
      : null;
  const recuperar = slugDoAcesso
    ? `/recuperar-senha?empresa=${encodeURIComponent(slugDoAcesso)}`
    : '/recuperar-senha';

  // O manipulador de evento nao devolve promessa. O envio roda em uma funcao
  // propria, para que nenhuma rejeicao escape sem tratamento.
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submit();
  }

  async function submit() {
    setError(null);
    setPendente(false);
    setSubmitting(true);

    try {
      await signIn(email, password, tenantSlug || slugDoAcesso || undefined);
    } catch (cause) {
      const segredo = trocaDaSenhaVencida(cause);
      if (segredo) {
        setTrocaVencida(segredo);
        return;
      }
      // O servidor pede a empresa quando a pessoa pertence a mais de uma. Ninguem
      // decora o identificador da propria empresa, entao a tela vai buscar a lista
      // com as mesmas credenciais e oferece a escolha.
      if (
        cause instanceof ApiError &&
        cause.code === ErrorCode.ValidationFailed &&
        cause.details?.['field'] === 'tenant_slug'
      ) {
        try {
          const lista = await workspacesApi.forCredentials(email, password);
          setEmpresas(lista);
          setTenantSlug(lista[0]?.tenant.slug ?? '');
          setError('Escolha a empresa em que voce quer entrar.');
          return;
        } catch {
          // Sem a lista, o campo de texto continua sendo um caminho possivel.
          setEmpresas([]);
        }
      }
      setPendente(ativacaoPendente(cause));
      setError(messageFor(cause));
    } finally {
      setSubmitting(false);
    }
  }

  const janela = manutencao.data?.under_maintenance ? manutencao.data.current : null;
  const proxima = manutencao.data?.next ?? null;

  return (
    <PublicFrame portal={portal}>
      <h1 className="text-xl font-semibold text-ink-900">{titulos[variante]}</h1>
      <p className="mt-1 mb-7 text-sm text-ink-500">Credenciais de acesso</p>

      <div className="mb-4 flex flex-col gap-3">
        {aviso ? <Alert tone="success">{aviso}</Alert> : null}
        {janela ? (
          <Alert tone="warning" title="Plataforma em manutencao">
            {janela.message} Durante a manutencao, so a administracao da plataforma usa
            o sistema.
          </Alert>
        ) : null}
        {reconhecida?.warn_maintenance && reconhecida.maintenance_message ? (
          <Alert tone="warning" title="Aviso de manutencao">
            {reconhecida.maintenance_message}
          </Alert>
        ) : null}
        {!janela && proxima ? (
          <Alert tone="info" title="Manutencao programada">
            {proxima.message} Inicio em {quando(proxima.starts_at)}.
          </Alert>
        ) : null}
      </div>

      {corretorFechado ? (
        <Alert tone="info" title="Tela indisponivel">
          {variante === 'assessor'
            ? 'A tela de acesso da assessoria nao esta disponivel para esta empresa.'
            : 'A tela de acesso do corretor nao esta disponivel para esta empresa.'}{' '}
          <Link
            to={`/entrar/${encodeURIComponent(slugDoAcesso)}`}
            className="font-medium underline"
          >
            Use o acesso principal
          </Link>
          .
        </Alert>
      ) : trocaVencida ? (
        <TrocaDaSenhaVencida
          segredo={trocaVencida}
          onCancelar={() => {
            setTrocaVencida(null);
            setPassword('');
          }}
          onTrocada={async (novaSenha) => {
            setTrocaVencida(null);
            setPassword('');
            try {
              await signIn(email, novaSenha, tenantSlug || slugDoAcesso || undefined);
            } catch (cause) {
              setError(messageFor(cause));
            }
          }}
        />
      ) : (
        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
          {error ? <Alert tone={pendente ? 'warning' : 'danger'}>{error}</Alert> : null}
          {pendente ? <ReenvioDaAtivacao email={email} /> : null}

          <Field
            label="Email"
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Digite seu e-mail"
          />

          <Field
            label="Senha"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Digite sua senha"
          />

          {empresas !== null && empresas.length > 0 ? (
            <Select
              label="Empresa"
              name="tenant_slug"
              value={tenantSlug}
              hint="Voce tem acesso a mais de uma empresa."
              options={empresas.map((item) => ({
                value: item.tenant.slug,
                label: item.tenant.name,
              }))}
              onChange={(event) => setTenantSlug(event.target.value)}
            />
          ) : null}

          {empresas !== null && empresas.length === 0 ? (
            <Field
              label="Empresa"
              name="tenant_slug"
              value={tenantSlug}
              onChange={(event) => setTenantSlug(event.target.value)}
              hint="Nao foi possivel listar suas empresas. Informe o identificador."
              placeholder="identificador-da-empresa"
            />
          ) : null}

          <div className="flex justify-end">
            <Link
              to={recuperar}
              className="text-xs font-medium text-brand-600 hover:underline"
            >
              Esqueci minha senha
            </Link>
          </div>

          <Button type="submit" size="lg" loading={submitting} className="w-full">
            Entrar
          </Button>
        </form>
      )}

      <p className="mt-6 text-center text-xs text-ink-500">
        Nao tem acesso?{' '}
        {reconhecida?.support_url ? (
          <a
            href={reconhecida.support_url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-brand-600 hover:underline"
          >
            Fale com o suporte
          </a>
        ) : (
          'Peca o acesso a quem administra a sua empresa.'
        )}
      </p>

      {autocadastro && variante !== 'assessor' ? (
        <p className="mt-3 text-center text-xs text-ink-500">
          Sua corretora ainda nao trabalha com a gente?{' '}
          <Link
            to={autocadastro}
            className="font-medium text-brand-600 hover:underline"
          >
            Cadastre sua corretora
          </Link>
        </p>
      ) : null}

      <p className="mt-6 text-center text-2xs text-ink-400">
        O acesso e monitorado e registrado em trilha de auditoria.
      </p>
    </PublicFrame>
  );
}
